import { useEffect, useRef } from 'react';
import IconRightArrow from '../icons/IconRightArrow';
import { useCart } from '../hooks/useCart';
import { formatCurrency } from '../helpers/formatters';
import { useLang } from '../hooks/useLanguage';
import { ShowIsBreakingOutFees } from '../../openapi/model/showIsBreakingOutFees';
import { ShowPriceListingMode } from '../../openapi/model/showPriceListingMode';
import { calculateProcessingFee } from '../helpers/calculateProcessingFee';
import PriceLoader from './PriceLoader';

const MobileBottomBar = ({
    nextStep,
    label,
    disabled,
    showFees,
    isBreakingOutFees = ShowIsBreakingOutFees.NUMBER_0,
    priceListingMode = ShowPriceListingMode.gross,
    cardProcessingFee = null,
    verification = null,
    disabledReasons = [],
}) => {
    const containerRef = useRef(null);
    const startYRef = useRef(null);
    const isSwipeActive = useRef(false);
    const { t } = useLang();
    const {
        getFinalPrice,
        getFinalPriceAfterAdjustment,
        getTaxesAndFeesAggregated,
        appliedCoupon,
        totalPrice: itemsNetTotal,
        isPricingLoading,
    } = useCart();
    const showBreakdown =
        (priceListingMode === ShowPriceListingMode.net ||
            isBreakingOutFees !== ShowIsBreakingOutFees.NUMBER_0) &&
        itemsNetTotal > 0;
    const totalAfterAdjustment = getFinalPriceAfterAdjustment(showFees);
    // Net inline discount = subtotal+fees minus the post-discount,
    // post-verification total. Positive = discount, negative = surcharge.
    const discount = getFinalPrice(showFees) - totalAfterAdjustment;
    let totalTaxesAndFees = 0;
    let estimatedProcessingFee = 0;
    if (showBreakdown) {
        const { totalFeeAmount, totalTaxAmount } = getTaxesAndFeesAggregated(showFees);
        totalTaxesAndFees = totalFeeAmount + totalTaxAmount;
        if (cardProcessingFee) {
            estimatedProcessingFee = calculateProcessingFee(totalAfterAdjustment, {
                percentage: Number(cardProcessingFee.percentage) || 0,
                usd: Number(cardProcessingFee.usd) || 0,
            });
        }
    }
    const displayedTotal = totalAfterAdjustment + estimatedProcessingFee;
    const isVerificationBlocking = verification?.status === 'error';
    const isVerificationLoading = verification?.status === 'loading';

    useEffect(() => {
        const onTouchStart = (e) => {
            const container = containerRef.current;
            if (!container) return;

            if (container.contains(e.target)) {
                isSwipeActive.current = true;
                startYRef.current = e.touches ? e.touches[0].clientY : e.clientY;
            } else {
                isSwipeActive.current = false;
                startYRef.current = null;
            }
        };

        const onTouchEnd = (e) => {
            if (!isSwipeActive.current || startYRef.current === null) return;

            const endY = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
            const deltaY = startYRef.current - endY;

            if (Math.abs(deltaY) < 10) {
                startYRef.current = null;
                isSwipeActive.current = false;
                return;
            }

            const container = containerRef.current;
            const parent = container?.closest('.fireknife-step__aside');
            if (!parent) return;

            if (deltaY > 0) {
                parent.classList.add('fireknife-step__aside--is-expanded');
                parent.scrollTop = 0;
            } else {
                parent.classList.remove('fireknife-step__aside--is-expanded');
            }

            startYRef.current = null;
            isSwipeActive.current = false;
        };

        window.addEventListener('touchstart', onTouchStart);
        window.addEventListener('touchend', onTouchEnd);
        window.addEventListener('mousedown', onTouchStart);
        window.addEventListener('mouseup', onTouchEnd);

        return () => {
            window.removeEventListener('touchstart', onTouchStart);
            window.removeEventListener('touchend', onTouchEnd);
            window.removeEventListener('mousedown', onTouchStart);
            window.removeEventListener('mouseup', onTouchEnd);
        };
    }, []);

    return (
        <div ref={containerRef} className="fireknife-mobile-bottom-bar">
            <div className="fireknife-mobile-bottom-bar__drag" aria-hidden="true"></div>
            {showBreakdown && (
                <div className="fireknife-mobile-bottom-bar__breakdown">
                    <div className="fireknife-mobile-bottom-bar__breakdown-row">
                        <span>{t('widgetBookingSummary.subtotalNet')}</span>
                        <span>
                            {isPricingLoading ? (
                                <PriceLoader />
                            ) : (
                                formatCurrency(itemsNetTotal / 100, t('widgetLocale'))
                            )}
                        </span>
                    </div>
                    {totalTaxesAndFees > 0 && (
                        <div className="fireknife-mobile-bottom-bar__breakdown-row">
                            <span>{t('widgetBookingSummary.bookingFee')}</span>
                            <span>
                                {isPricingLoading ? (
                                    <PriceLoader />
                                ) : (
                                    formatCurrency(totalTaxesAndFees / 100, t('widgetLocale'))
                                )}
                            </span>
                        </div>
                    )}
                    {estimatedProcessingFee > 0 && (
                        <div className="fireknife-mobile-bottom-bar__breakdown-row">
                            <span>{t('widgetReservationInfo.paymentDetails.processingFee')}</span>
                            <span>
                                {isPricingLoading ? (
                                    <PriceLoader />
                                ) : (
                                    formatCurrency(
                                        estimatedProcessingFee / 100,
                                        t('widgetLocale'),
                                    )
                                )}
                            </span>
                        </div>
                    )}
                    {!isPricingLoading && discount !== 0 && (
                        <div className="fireknife-mobile-bottom-bar__breakdown-row">
                            <span>
                                {t('coupons.discount')}
                                {appliedCoupon ? ` (${appliedCoupon.code})` : ''}
                            </span>
                            <span>
                                {discount > 0 ? '-' : '+'}
                                {formatCurrency(Math.abs(discount) / 100, t('widgetLocale'))}
                            </span>
                        </div>
                    )}
                    {isVerificationLoading && (
                        <div className="fireknife-mobile-bottom-bar__breakdown-row">
                            <span>{t('errors.verifying')}</span>
                        </div>
                    )}
                </div>
            )}
            {/* Visibility is controlled by CSS: hidden unless the aside has
                the --is-expanded modifier, same trick as __breakdown above.
                Keeps the Total above the fold in the collapsed peek state. */}
            {disabledReasons && disabledReasons.length > 0 ? (
                <div role="alert" className="fireknife-verification-error">
                    {disabledReasons.map((reason, idx) => (
                        <div key={idx}>{reason}</div>
                    ))}
                </div>
            ) : (
                isVerificationBlocking &&
                verification?.errorMessage && (
                    <div role="alert" className="fireknife-verification-error">
                        {verification.errorMessage}
                    </div>
                )
            )}
            <div className="fireknife-mobile-bottom-bar__boxes">
                <div className="fireknife-mobile-bottom-bar__box fireknife-mobile-bottom-bar__box--1">
                    <div className="fireknife-mobile-bottom-bar__cart">
                        {t('common.total')}:
                        <span className="fireknife-mobile-bottom-bar__cart-text">
                            {isPricingLoading ? (
                                <PriceLoader />
                            ) : (
                                formatCurrency(displayedTotal / 100, t('widgetLocale'))
                            )}
                        </span>
                    </div>
                </div>
                <div className="fireknife-mobile-bottom-bar__box fireknife-mobile-bottom-bar__box--2">
                    <button
                        className="fireknife-mobile-bottom-bar__btn fireknife-btn fireknife-btn--primary"
                        type="button"
                        onClick={nextStep ? nextStep : () => {}}
                        disabled={
                            disabled ||
                            isVerificationBlocking ||
                            isVerificationLoading ||
                            isPricingLoading
                        }
                    >
                        <span className="fireknife-mobile-bottom-bar__btn-txt">{label}</span>
                        <IconRightArrow className="fireknife-mobile-bottom-bar__btn-icon" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MobileBottomBar;
